/* Hidden entrance state is set here, never in the markup. Both card faces stay in the DOM for assistive tech. */
(() => {
  const FALLBACK_MS = 1200;
  // Long enough for the first card's entrance to have landed, so the nudge reads as its own gesture.
  const TEASE_DELAY_MS = 700;
  const isTouch = () => window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let teaseClaimed = false;

  const setFlipped = (card, toggle, flipped) => {
    card.dataset.esFlipped = flipped ? 'true' : 'false';
    toggle.setAttribute('aria-pressed', flipped ? 'true' : 'false');
  };

  const initCard = (card) => {
    if (card.dataset.esCardReady) return;
    const toggle = card.querySelector('[data-es-toggle]');
    if (!toggle) return;
    card.dataset.esCardReady = 'true';

    toggle.addEventListener('click', (event) => {
      if (isTouch()) {
        setFlipped(card, toggle, card.dataset.esFlipped !== 'true');
        return;
      }
      // Hover already flips on pointer devices, so a mouse click must not pin the card open via :focus-within.
      if (event.detail > 0) toggle.blur();
    });

    card.addEventListener('mouseleave', () => {
      if (isTouch()) return;
      setFlipped(card, toggle, false);
    });
  };

  /* Touch-only first-visit nudge. pointerdown fires before click, so the card transitions from wherever the nudge had got to. */
  const initTease = (root) => {
    if (teaseClaimed) return;
    if (!isTouch() || prefersReducedMotion()) return;
    if (!('IntersectionObserver' in window)) return;

    const card = root.querySelector('[data-es-card]');
    if (!card) return;

    teaseClaimed = true;

    let timer = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        timer = setTimeout(() => {
          card.addEventListener('animationend', () => card.removeAttribute('data-es-tease'), {
            once: true,
          });
          card.dataset.esTease = 'true';
        }, TEASE_DELAY_MS);
      },
      { threshold: 0.4 }
    );

    card.addEventListener(
      'pointerdown',
      () => {
        clearTimeout(timer);
        observer.disconnect();
        card.removeAttribute('data-es-tease');
      },
      { once: true }
    );

    observer.observe(card);
  };

  const initReveal = (root) => {
    if (root.dataset.esAnimate !== 'true') return;
    if (prefersReducedMotion()) return;

    root.setAttribute('data-es-reveal', '');

    let timer = 0;
    const show = () => {
      clearTimeout(timer);
      root.setAttribute('data-es-reveal', 'in');
    };

    if (!('IntersectionObserver' in window)) {
      show();
      return;
    }

    timer = setTimeout(show, FALLBACK_MS);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          show();
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(root);
  };

  const init = (root) => {
    root.querySelectorAll('[data-es-card]').forEach(initCard);

    if (root.dataset.esReady) return;
    root.dataset.esReady = 'true';
    initReveal(root);
    initTease(root);
  };

  const boot = () => document.querySelectorAll('[data-bronte-every-sock]').forEach(init);

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', boot);
})();
